import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import {
  INSTITUTION_ORIGINS,
  PORTAL_BRAND,
  institutionFromUser,
  isAllowedOnThisPortal,
  redirectToInstitutionPortal,
} from '../config/institution';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.login({ email, password });
      const user = result.user;
      const mustChange = false;

      // Cross-portal: CNS credentials on ANR login → redirect to CNS
      if (redirectToInstitutionPortal(result.token, user, { mustChangePassword: mustChange })) {
        return;
      }

      if (!isAllowedOnThisPortal(user)) {
        authService.logout();
        const inst = institutionFromUser(user);
        if (inst === 'cns') {
          setError(`Compte CNS detecte. Utilisez le portail CNS (${INSTITUTION_ORIGINS.cns}).`);
        } else {
          setError('Acces refuse. Ce portail est reserve aux comptes ANR.');
        }
        return;
      }

      navigate('/');
      window.location.reload();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please try again.';
      if (errorMessage.includes('Invalid email or password')) {
        setError('Email ou mot de passe invalide.');
      } else if (errorMessage.includes('Account temporarily locked')) {
        setError(errorMessage);
      } else if (errorMessage.includes('Account is inactive')) {
        setError('Compte inactif. Contactez le support.');
      } else if (errorMessage.includes('Access denied')) {
        setError('Acces refuse. Permissions insuffisantes.');
      } else {
        setError(errorMessage);
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-teal-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-block p-3 bg-white rounded-full mb-4">
            <svg className="w-12 h-12 text-emerald-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white">{PORTAL_BRAND.fullName}</h1>
          <p className="text-emerald-200 mt-2">{PORTAL_BRAND.subtitle}</p>
          <p className="text-emerald-100/80 text-xs mt-3">
            Connexion partagee CNS / ANR — redirection automatique selon votre institution
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Connexion agent</h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email professionnel
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-transparent outline-none transition"
                placeholder="agent@anr.cd"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600 focus:border-transparent outline-none transition"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-lg transition disabled:opacity-50"
            >
              {loading ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t text-center text-xs text-gray-500 space-y-1">
            <p>Comptes CNS → portail CNS automatique</p>
            <p>Comptes ANR → ce tableau de bord</p>
            <a className="text-emerald-700 underline" href={INSTITUTION_ORIGINS.cns}>
              Ouvrir le portail CNS
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
